/**
 * La vérification publique d'un certificat (docs/12 §5).
 *
 * **Le seul écran de l'application qui s'adresse à quelqu'un sans compte.** Il
 * en découle tout le reste :
 *
 * - il n'affiche que ce que l'API rend — nom, cours, date, validité — et l'API
 *   n'a pas de champ pour rendre autre chose ;
 * - un numéro inconnu et un numéro mal formé produisent le **même** message,
 *   parce qu'ils produisent la même réponse ;
 * - un certificat révoqué **s'affiche**, avec sa date. C'est toute l'utilité du
 *   geste : le porteur d'un faux et celui d'un titre retiré ne doivent pas
 *   recevoir la même réponse.
 *
 * Le numéro se saisit aussi à la main : quelqu'un qui lit une feuille imprimée
 * n'a pas de lien à cliquer.
 */
import { useNavigate } from "@tanstack/react-router";
import { useState, type SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

import { useCertificateCheck } from "@/features/learning/api/useCertificates";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { Field } from "@/shared/ui/Field";
import { Skeleton } from "@/shared/ui/Skeleton";

interface Props {
  serial?: string;
}

export function VerifyCertificatePage({ serial = "" }: Props) {
  const { t, i18n } = useTranslation("learning");
  const navigate = useNavigate();
  const [typed, setTyped] = useState(serial);
  const check = useCertificateCheck(serial);

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "long" }).format(new Date(value));

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const cleaned = typed.trim();
    if (cleaned.length > 0) {
      void navigate({ to: "/certificates/verify/$serial", params: { serial: cleaned } });
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 px-4 py-10">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t("verify.title")}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t("verify.subtitle")}</p>
      </header>

      <Card>
        <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <Field
            id="serial"
            name="serial"
            label={t("verify.serialLabel")}
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
            autoComplete="off"
            spellCheck={false}
            placeholder="XXXX-XXXX-XXXX-XXXX"
            className="flex-1 font-mono tracking-wide"
            hint={t("verify.serialHint")}
          />
          <Button type="submit">{t("verify.submit")}</Button>
        </form>
      </Card>

      {serial.length > 0 && check.isPending && (
        <Skeleton className="h-40 w-full" label={t("verify.title")} />
      )}

      {check.isError && (
        // Volontairement **le même message** pour un numéro inconnu et pour un
        // numéro mal formé : les distinguer donnerait à qui balaie l'espace des
        // numéros un indice sur le format attendu.
        <Card>
          <p className="text-sm text-slate-700 dark:text-slate-300">{t("verify.unknown")}</p>
        </Card>
      )}

      {check.isSuccess && (
        <Card title={check.data.course_title}>
          <div className="space-y-4">
            {/* Le verdict est **écrit**, jamais seulement coloré : « authentique »
                et « retirée » se lisent sans distinguer le vert du rouge
                (WCAG 1.4.1). La couleur ne fait que confirmer. */}
            <p
              className={
                check.data.is_valid
                  ? "rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100"
                  : "rounded-md bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950 dark:text-red-200"
              }
            >
              {check.data.is_valid
                ? t("verify.valid")
                : t("verify.revoked", {
                    date: check.data.revoked_at === null ? "" : formatDate(check.data.revoked_at),
                  })}
            </p>
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">{t("verify.holder")}</dt>
                <dd className="text-sm">{check.data.student_name}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">
                  {t("certificates.issuedOn")}
                </dt>
                <dd className="text-sm">{formatDate(check.data.issued_at)}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">
                  {t("certificates.serial")}
                </dt>
                <dd className="font-mono text-sm tracking-wide">{check.data.serial}</dd>
              </div>
            </dl>
          </div>
        </Card>
      )}
    </div>
  );
}
